import { api } from "@/lib/api/client";

export interface Organisation {
  id: string;
  logo: string | null;
  slug: string;
  name: string;
  patient_code_prefix: string;
  patient_code_digits: number;
  country: string;
  timezone: string;
  currency: string;
  sale_invoice_prefix: string;
  proforma_prefix: string;
  client_code_prefix: string;
  reset_numbering_yearly: boolean;
  locale: string;
  primary_color: string;
  address: string;
  phone: string;
  phone_secondary: string;
  email: string;
  facebook: string;
  agrement_number: string;
  exploitation_number: string;
  document_header_source: "account" | "clinic";
  show_document_logo: boolean;
  show_legal_footer: boolean;
  default_invoice_note: string;
  specialty: "general" | "optique";
  /** Packs de spécialité cumulés, spécialité principale comprise. */
  packs?: ("general" | "optique")[];
  stock_tracking: boolean;
  display_config: DisplayConfig;
  /** Rang dans les listes de cliniques : plus petit = plus haut (défaut 100). */
  display_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

/** Config d'affichage par clinique : listes d'éléments MASQUÉS. */
export interface DisplayConfig {
  hidden_nav?: string[];
  hidden_tabs?: Record<string, string[]>;
}

export interface OrganisationPayload {
  slug: string;
  name: string;
  patient_code_prefix: string;
  patient_code_digits?: number;
  country?: string;
  currency?: string;
  sale_invoice_prefix?: string;
  timezone?: string;
  primary_color?: string;
  address?: string;
  phone?: string;
  phone_secondary?: string;
  email?: string;
  facebook?: string;
  agrement_number?: string;
  exploitation_number?: string;
  specialty?: "general" | "optique";
  packs?: ("general" | "optique")[];
  stock_tracking?: boolean;
  display_config?: DisplayConfig;
  display_order?: number;
  is_active?: boolean;
}

export interface AccessibleResponse {
  mode: "all" | "memberships";
  can_switch_freely: boolean;
  active_organisation_id: string | null;
  /** Fiches COMPLÈTES (OrganisationSerializer) : l'en-tête et le pied des
   *  documents imprimés y lisent logo, adresse, téléphones et agrément —
   *  /organisations/{id}/ étant réservé à l'éditeur. */
  organisations: Organisation[];
  /** « Toutes les cliniques » proposé (propriétaire du compte, module
   *  `account_wide_view`, ADR 0065). Absent (serveur ancien) = non. */
  can_select_all_clinics?: boolean;
  /** La session est en « Toutes les cliniques ». */
  account_scope?: boolean;
}

export interface OrganisationStats {
  patients: number;
  users: number;
  sales: number;
  total_invoiced: string;
  total_paid: string;
  outstanding: string;
}

export interface OrganisationFilters {
  search?: string;
}

export const organisationsApi = {
  list: async (filters: OrganisationFilters = {}) => {
    const params: Record<string, string> = {};
    if (filters.search) params["search"] = filters.search;
    const { data } = await api.get<{ count: number; results: Organisation[] }>("/organisations/", {
      params,
    });
    return data;
  },
  get: async (id: string) => {
    const { data } = await api.get<Organisation>(`/organisations/${id}/`);
    return data;
  },
  create: async (payload: OrganisationPayload) => {
    const { data } = await api.post<Organisation>("/organisations/", payload);
    return data;
  },
  update: async (id: string, payload: Partial<OrganisationPayload>) => {
    const { data } = await api.patch<Organisation>(`/organisations/${id}/`, payload);
    return data;
  },
  uploadLogo: async (id: string, file: File) => {
    const form = new FormData();
    form.append("logo", file);
    // Content-Type multipart explicite : sinon le défaut JSON du client casse
    // l'upload (415). Axios ajoute la boundary.
    const { data } = await api.post<Organisation>(`/organisations/${id}/logo/`, form, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return data;
  },
  accessible: async () => {
    const { data } = await api.get<AccessibleResponse>("/organisations/accessible/");
    return data;
  },
  stats: async (id: string) => {
    const { data } = await api.get<OrganisationStats>(`/organisations/${id}/stats/`);
    return data;
  },
};
